/** XeAgents.ai - Auto-inject Analytics from Admin Settings **/
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

// Extend Window interface
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    clarity?: (...args: any[]) => void;
  }
}

interface AnalyticsConfig {
  ga4MeasurementId: string | null;
  clarityProjectId: string | null;
}

/**
 * Component that auto-injects Google Analytics 4 and Microsoft Clarity
 * based on admin settings configured in Admin → Analytics Scripts → Quick Setup.
 *
 * Analytics IDs are fetched from the database via /api/public/analytics-config
 */
export function AnalyticsAutoInject() {
  const [injected, setInjected] = useState({ ga4: false, clarity: false });

  // Fetch analytics config from API (admin configured values)
  const { data: config } = useQuery<AnalyticsConfig>({
    queryKey: ["/api/public/analytics-config"],
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });

  useEffect(() => {
    // Only run in browser
    if (typeof window === "undefined") return;

    // Use IDs from admin configuration (database)
    const ga4Id = config?.ga4MeasurementId;
    const clarityId = config?.clarityProjectId;

    // Inject Google Analytics 4
    if (ga4Id && ga4Id.startsWith("G-") && !injected.ga4) {
      injectGA4(ga4Id);
      setInjected(prev => ({ ...prev, ga4: true }));
    }

    // Inject Microsoft Clarity
    if (clarityId && clarityId.length > 0 && !injected.clarity) {
      injectClarity(clarityId);
      setInjected(prev => ({ ...prev, clarity: true }));
    }
  }, [config, injected]);

  return null;
}

/**
 * Inject Google Analytics 4 script
 */
function injectGA4(measurementId: string): void {
  // Check if already injected
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`)) {
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer!.push(arguments);
  };

  // Set default consent (will be updated by CookieConsentBanner)
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });

  // Load gtag.js script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.setAttribute("data-analytics-auto", "ga4");
  document.head.appendChild(script);

  // Initialize GA4
  window.gtag("js", new Date());
  window.gtag("config", measurementId, {
    send_page_view: true,
  });

  console.log(`[Analytics] GA4 initialized: ${measurementId}`);
}

/**
 * Inject Microsoft Clarity script
 */
function injectClarity(projectId: string): void {
  // Check if already injected
  if (document.querySelector(`script[src*="clarity.ms/tag/${projectId}"]`)) {
    return;
  }

  // Clarity initialization script
  (function(c: any, l: any, a: any, r: any, i: any, t?: any, y?: any) {
    c[a] = c[a] || function() {
      (c[a].q = c[a].q || []).push(arguments);
    };
    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    t.setAttribute("data-analytics-auto", "clarity");
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", projectId);

  console.log(`[Analytics] Microsoft Clarity initialized: ${projectId}`);
}

export default AnalyticsAutoInject;
